import { useState, useEffect } from "react";
import { Box, Dialog, DialogTitle, DialogContent, IconButton, GlobalStyles } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ThemeProvider } from "@mui/material/styles";

import { AddBaZiPage } from "./components/ui/PersonInfoInput";
import WebBottomNavigation from "./components/ui/WebBottomNavigation";
import { baZiTheme } from "./components/data";
import RecordsPage from "./components/ui/RecordsPage";
import MyPage from "./components/ui/MyPage";

import { post_paipan, migrate_anonymous, get_me } from "./client/paipan";
import { enrichBaZiData } from "./utils/baziCalculator";
import AboutPage from "./components/ui/About";
import LoginDialog from "./components/ui/LoginDialog";
import MemberDialog from "./components/ui/MemberDialog";

function buildLocalRecord(data, res) {
  return {
    id: res.saved_id || `local-${Date.now()}`,
    name: data.name,
    gender: data.gender,
    birthday_str: data.birthday_str,
    use_zaozi: data.use_zaozi,
    use_true_solar: data.use_true_solar,
    longitude: data.longitude,
    birthplace: data.birthplace,
    category: data.category || "其他",
    computed_at: new Date().toISOString(),
    birthday_dt: res.birthday_dt,
    true_solar_time: res.true_solar_time,
    sheng_xiao: res.sheng_xiao,
    shun_ni: res.shun_ni,
    nian_gan: res.nian_gan,
    nian_zhi: res.nian_zhi,
    yue_gan: res.yue_gan,
    yue_zhi: res.yue_zhi,
    ri_gan: res.ri_gan,
    ri_zhi: res.ri_zhi,
    shi_gan: res.shi_gan,
    shi_zhi: res.shi_zhi,
    qiyun_str: res.qiyun_str,
    da_yun_start_year: res.da_yun_start_year,
    result: res,
  };
}

function App() {
  const [pageIndex, setPageIndex] = useState(0);
  const [mingPanData, setMingPanData] = useState();
  const [mingPanTrigger, setMingPanTrigger] = useState(0);
  const [recordsResetKey, setRecordsResetKey] = useState(0);
  const [lastInputData, setLastInputData] = useState(null);
  const [user, setUser] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [recordsInSubPage, setRecordsInSubPage] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);

  // AI 指令页面状态提升，避免切换 tab 后丢失
  const [aiZhiLingState, setAiZhiLingState] = useState({
    selectedDaYun: [],
    selectedLiuNian: [],
    includeLiuYue: false,
    includeLiuRi: false,
    includeLiuShi: false,
    previewText: "",
  });

  // 恢复登录态 + 初始化匿名 ID
  useEffect(() => {
    const saved = localStorage.getItem("user");
    if (saved) {
      try {
        setUser(JSON.parse(saved));
      } catch {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }
    }
    if (!localStorage.getItem("anonymous_id")) {
      localStorage.setItem("anonymous_id", crypto.randomUUID());
    }
    // 有 token 时刷新用户状态（获取最新会员信息）
    if (localStorage.getItem("token")) {
      get_me().then((res) => {
        if (res && res.user) {
          setUser(res.user);
          localStorage.setItem("user", JSON.stringify(res.user));
        }
      });
    }
  }, []);

  function handleIndexChange(e, v) {
    setPageIndex(v);
    if (v === 1) {
      setRecordsResetKey((prev) => prev + 1);
    }
  }

  function handleMemberClick() {
    if (!user) {
      setLoginOpen(true);
      return;
    }
    setMemberOpen(true);
  }

  return (
    <ThemeProvider theme={baZiTheme}>
      <GlobalStyles styles={{ body: { overflowX: "hidden" } }} />
      <Box
        sx={{
          width: "100%",
          maxWidth: { md: 600 },
          mx: { md: "auto" },
          pt: 0,
          pb: "70px",
          position: "relative",
        }}
      >
        <Box sx={{ display: pageIndex === 0 ? "block" : "none" }}>
          <AddBaZiPage
            defaultValues={lastInputData}
            onPaiPan={async (data) => {
              setLastInputData(data);
              const res = await post_paipan(data);
              if (res) {
                enrichBaZiData(res);
                setMingPanData(res);
                setMingPanTrigger((prev) => prev + 1);
                setAiZhiLingState({
                  selectedDaYun: [],
                  selectedLiuNian: [],
                  includeLiuYue: false,
                  includeLiuRi: false,
                  includeLiuShi: false,
                  previewText: "",
                });
                // 未登录用户同时保存到浏览器本地
                if (!user) {
                  const records = JSON.parse(localStorage.getItem("bazi_anonymous_records") || "[]");
                  records.unshift(buildLocalRecord(data, res));
                  localStorage.setItem("bazi_anonymous_records", JSON.stringify(records));
                  // 同步后端返回的 anonymous_id（首次生成时）
                  if (res.anonymous_id) {
                    localStorage.setItem("anonymous_id", res.anonymous_id);
                  }
                }
                setPageIndex(1);
              } else {
                console.log("fail to get ming pan data from server");
              }
            }}
          />
        </Box>
        <Box sx={{ display: pageIndex === 1 ? "block" : "none" }}>
          <RecordsPage
            user={user}
            mingPanTrigger={mingPanTrigger}
            recordsResetKey={recordsResetKey}
            initialMingPanData={mingPanData}
            aiZhiLingState={aiZhiLingState}
            setAiZhiLingState={setAiZhiLingState}
            onLoginClick={() => setLoginOpen(true)}
            onSubPageChange={setRecordsInSubPage}
            onMemberClick={handleMemberClick}
          />
        </Box>
        <Box sx={{ display: pageIndex === 2 ? "block" : "none" }}>
          <MyPage
            user={user}
            onLoginClick={() => setLoginOpen(true)}
            onLogout={() => {
              setUser(null);
              localStorage.removeItem("token");
              localStorage.removeItem("user");
            }}
            onAboutClick={() => setAboutOpen(true)}
            onMemberClick={handleMemberClick}
          />
        </Box>
      </Box>

      {!(pageIndex === 1 && recordsInSubPage) && (
        <WebBottomNavigation value={pageIndex} onChange={handleIndexChange} />
      )}

      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={async (u) => {
          // 认领服务器上的匿名记录到当前账号
          const anonymousId = localStorage.getItem("anonymous_id");
          const localRecords = JSON.parse(localStorage.getItem("bazi_anonymous_records") || "[]");
          if (anonymousId && localRecords.length > 0) {
            const recordIds = localRecords.map(r => r.id).filter(id => typeof id === "number");
            if (recordIds.length > 0) {
              await migrate_anonymous(anonymousId, recordIds);
            }
          }
          localStorage.removeItem("bazi_anonymous_records");
          localStorage.removeItem("anonymous_id");
          setUser(u);
          setRecordsResetKey((prev) => prev + 1);
        }}
      />

      <MemberDialog
        open={memberOpen}
        onClose={() => setMemberOpen(false)}
        user={user}
        onRedeemSuccess={(u) => {
          setUser(u);
          localStorage.setItem("user", JSON.stringify(u));
        }}
      />

      <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
          关于
          <IconButton
            onClick={() => setAboutOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <AboutPage />
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
